import { useState } from 'react';

import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useCreateQualityConfig,
  useQualityConfigDetail,
  useUpdateQualityConfig,
} from '@/hooks/useQualityConfigs';

const DEFAULT_RUBRIC = JSON.stringify(
  {
    type: 'object',
    properties: {
      is_good_call: { type: 'boolean', description: 'Overall: no significant issues' },
      has_hallucination: {
        type: 'boolean',
        description: 'Agent fabricated information not in instructions',
      },
      has_agent_silence: {
        type: 'boolean',
        nullable: true,
        description: 'Agent went silent unexpectedly (audio-only)',
      },
      rating_score: {
        type: 'integer',
        description: 'Overall quality 1-10',
      },
      reasoning: {
        type: 'string',
        description: 'One-paragraph rationale for ops',
      },
    },
    required: ['is_good_call', 'has_hallucination', 'rating_score', 'reasoning'],
  },
  null,
  2,
);

const DEFAULT_PROMPT =
  "You assess the AI agent's performance on a call. Use the transcript and audio (if attached) to fill in every flag and score per the schema. The audio-only flags (silence, audio glitches, accent) should be null when audio is not provided.";

export const QualityConfigEditPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: routeId } = useParams<{ id: string }>();
  const isNew = !routeId || routeId === 'new';

  usePageTitle(isNew ? 'New Quality Config' : 'Quality Config');

  const { data: detail, isLoading } = useQualityConfigDetail(isNew ? undefined : routeId);
  const createMutation = useCreateQualityConfig();
  const updateMutation = useUpdateQualityConfig();

  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [modelName, setModelName] = useState('gemini-2.5-pro');
  const [temperature, setTemperature] = useState('0.2');
  const [rubricText, setRubricText] = useState(DEFAULT_RUBRIC);
  const [rubricVersion, setRubricVersion] = useState('v1');
  const [isDefault, setIsDefault] = useState(false);
  const [injectCurrentDate, setInjectCurrentDate] = useState(false);
  const [includeToolExecutions, setIncludeToolExecutions] = useState(false);
  const [rubricError, setRubricError] = useState<string | null>(null);

  // Sync local form state from server-loaded detail (React-recommended pattern
  // for "adjust state when a derived value changes").
  const [prevDetail, setPrevDetail] = useState(detail);
  if (detail !== prevDetail) {
    setPrevDetail(detail);
    if (detail) {
      setName(detail.name);
      setPrompt(detail.prompt);
      setModelName(detail.model_name);
      setTemperature(String(detail.temperature));
      setRubricText(JSON.stringify(detail.rubric, null, 2));
      setRubricVersion(detail.rubric_version);
      setIsDefault(detail.is_default);
      setInjectCurrentDate(detail.inject_current_date ?? false);
      setIncludeToolExecutions(detail.include_tool_executions ?? false);
    }
  }

  const parseRubric = (): Record<string, unknown> | null => {
    try {
      const parsed = JSON.parse(rubricText);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setRubricError('Rubric must be a JSON object');
        return null;
      }
      setRubricError(null);
      return parsed as Record<string, unknown>;
    } catch (err) {
      setRubricError(err instanceof Error ? err.message : 'Invalid JSON');
      return null;
    }
  };

  const handleSave = async () => {
    const rubric = parseRubric();
    if (!rubric) return;
    if (!name.trim()) {
      toast({ variant: 'destructive', title: 'Missing name', description: 'Add a config name.' });
      return;
    }
    if (!rubricVersion.trim()) {
      toast({
        variant: 'destructive',
        title: 'Missing rubric version',
        description: 'Bump this when the rubric changes — drives backfill detection.',
      });
      return;
    }

    const payload = {
      name,
      prompt,
      model_name: modelName,
      temperature: Number(temperature) || 0,
      rubric,
      rubric_version: rubricVersion,
      is_default: isDefault,
      inject_current_date: injectCurrentDate,
      include_tool_executions: includeToolExecutions,
    };

    try {
      if (isNew) {
        const created = await createMutation.mutateAsync(payload);
        toast({
          variant: 'success',
          title: 'Created',
          description: `Config '${created.name}' created.`,
        });
      } else {
        await updateMutation.mutateAsync({ id: routeId, data: payload });
        toast({ variant: 'success', title: 'Saved', description: `Config '${name}' updated.` });
      }
      navigate('/quality-configs');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast({ variant: 'destructive', title: 'Save failed', description: msg });
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/quality-configs')}>
        <ArrowLeft className="mr-2 h-4 w-4" /> Back
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{isNew ? 'New quality config' : `Edit: ${detail?.name}`}</CardTitle>
          <CardDescription>
            Defines the prompt, model, and rubric (JSON Schema) for the QualityAgent. Booleans
            become flags; numbers and strings land in data; the dedicated `reasoning` field is
            stored in a typed text column for the ops UI. Bump rubric_version when the schema
            changes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="V12 main quality"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Model</label>
              <Input
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                placeholder="gemini-2.5-pro"
                className="font-mono text-xs"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Temperature</label>
              <Input
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                type="number"
                step="0.1"
                min="0"
                max="2"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Rubric version</label>
            <Input
              value={rubricVersion}
              onChange={(e) => setRubricVersion(e.target.value)}
              placeholder="v1"
              className="font-mono text-xs"
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
            <span className="text-sm">{isDefault ? 'Default for this DB' : 'Not the default'}</span>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Switch checked={injectCurrentDate} onCheckedChange={setInjectCurrentDate} />
              <span className="text-sm">
                {injectCurrentDate ? 'Inject current date' : "Don't inject current date"}
              </span>
            </div>
            <p className="text-muted-foreground text-xs">
              Prepends the call&apos;s date (anchored on executed_at) so relative-date claims like
              &quot;tomorrow&quot; are judged against the day the call happened.
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Switch checked={includeToolExecutions} onCheckedChange={setIncludeToolExecutions} />
              <span className="text-sm">
                {includeToolExecutions ? 'Include tool executions' : 'Tool-blind (no tool log)'}
              </span>
            </div>
            <p className="text-muted-foreground text-xs">
              Appends a TOOL EXECUTION LOG of recorded tool invocations so the judge can flag tools
              that should have fired but didn&apos;t. Off = fully tool-blind (tool rows are also
              stripped from the transcript).
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={6}
              className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Rubric (JSON Schema)</label>
            <textarea
              value={rubricText}
              onChange={(e) => setRubricText(e.target.value)}
              rows={20}
              className="border-input bg-background w-full rounded-md border px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            />
            {rubricError && <p className="text-destructive text-xs">{rubricError}</p>}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => navigate('/quality-configs')}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {createMutation.isPending || updateMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
